/**
 * Which covers and editions couples actually pick.
 *
 * TWO DIMENSIONS, ONE TABLE. `style_key` is the book COVER and `pdf_template`
 * is the EDITION (the binding); they are separate columns on wedding_books and
 * a book has one of each, so the rows are labelled by type rather than merged
 * into a single ranking that would double-count every book.
 *
 * SHARE IS OF THE RANGE, TREND IS AGAINST THE PRIOR RANGE. The server computes
 * both, and returns `share_in_range: null` — not 0 — when no book was created
 * in the window at all: there is nothing to take a share of. That renders '—'.
 *
 * The "New" pill comes from `wedding_catalogue_items.created_at`, which exists
 * only where migration 457 has been applied. When it has not, `is_new` is NULL
 * everywhere and no pill is drawn — the absence of a pill never means "old".
 */

import React from 'react';
import {
  Box, Card, CardContent, Chip, LinearProgress, Link, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RemoveIcon from '@mui/icons-material/Remove';
import { useNavigate } from 'react-router-dom';
import { num } from '../weddingFormat';
import type { TemplateUsage, TemplateUsageRow } from '../weddingApi';

interface Props {
  usage: TemplateUsage | undefined;
  rangeLabel: string;
}

/** 'garden_botanical' → 'Garden botanical'. '(unset)' is left as the server said it. */
function humanise(key: string): string {
  if (!key || key === '(unset)') return 'Not chosen';
  const spaced = key.replace(/[_-]+/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

const TrendMark: React.FC<{ row: TemplateUsageRow }> = ({ row }) => {
  const title = `${row.books_in_range} in this range vs ${row.books_prev_range} in the previous one`;
  if (row.trend === 'up') {
    return (
      <Tooltip title={title}>
        <ArrowUpwardIcon fontSize="small" color="success" aria-label="up on the previous period" />
      </Tooltip>
    );
  }
  if (row.trend === 'down') {
    return (
      <Tooltip title={title}>
        <ArrowDownwardIcon fontSize="small" color="error" aria-label="down on the previous period" />
      </Tooltip>
    );
  }
  return (
    <Tooltip title={title}>
      <RemoveIcon fontSize="small" color="disabled" aria-label="unchanged" />
    </Tooltip>
  );
};

const TemplateUsageCard: React.FC<Props> = ({ usage, rangeLabel }) => {
  const navigate = useNavigate();

  const rows: { row: TemplateUsageRow; type: string; tone: 'primary' | 'secondary' }[] = [
    ...(usage?.style_key || []).map((row) => ({ row, type: 'Book cover', tone: 'primary' as const })),
    ...(usage?.pdf_template || []).map((row) => ({ row, type: 'Edition', tone: 'secondary' as const })),
  ];

  return (
    <Card sx={{ height: '100%', borderRadius: '16px' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Template usage
          </Typography>
          <Link
            component="button"
            type="button"
            variant="body2"
            underline="hover"
            onClick={() => navigate('/dashboard/wedding/catalogue')}
          >
            Manage templates →
          </Link>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Which covers and editions couples pick. Share and trend are measured over {rangeLabel};
          the count is every book ever.
        </Typography>

        {rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No book has chosen a cover or an edition yet.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Template</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Books using</TableCell>
                <TableCell sx={{ width: 140 }}>Share in range</TableCell>
                <TableCell align="right">Trend</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(({ row, type, tone }) => (
                <TableRow key={`${type}:${row.key}`}>
                  <TableCell>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {humanise(row.key)}
                      </Typography>
                      {row.is_new === true && (
                        <Tooltip title="Added to the catalogue in the last 30 days.">
                          <Chip size="small" color="secondary" variant="outlined" label="New" />
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">{type}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{num(row.books_total).toLocaleString()}</Typography>
                  </TableCell>
                  <TableCell>
                    {row.share_in_range === null ? (
                      <Tooltip title="No book was created in this range, so there is no share to take.">
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      </Tooltip>
                    ) : (
                      <Stack spacing={0.25}>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(100, Math.round(row.share_in_range * 100))}
                          color={tone}
                          sx={{ height: 7, borderRadius: 4 }}
                          aria-label={`${humanise(row.key)} share`}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {Math.round(row.share_in_range * 100)}%
                        </Typography>
                      </Stack>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <TrendMark row={row} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {usage && usage.catalogue_available === false && (
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1.5 }}>
            The catalogue table is not in this database, so no template can be marked as new. The counts
            above are read from the books themselves and are unaffected.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default TemplateUsageCard;
